import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames/bind';
import Alert from 'js/components/alert/alert';
import Box from 'js/components/box/box';
import ClickArea from 'js/components/click-area/click-area';
import Container from 'js/components/container/container';
import Col from 'js/components/grid/column';
import Row from 'js/components/grid/row';
import IndicatorBar from 'js/components/indicator-bar/indicator-bar';
import Modal from 'js/components/modal/modal';
import Pill from 'js/components/pill/pill';
import Text from 'js/components/text/text';
import TopicButton from 'js/components/button/topic-button';
import styles from '../quick-start.module.scss';

const SCALE_LOW_LEVEL = 4;
const SCALE_MEDIUM_LEVEL = 8;

const regularPricing = {
    video: '$2.00 CPM',
    display: '$0.65 CPM',
};

const cx = classNames.bind(styles);

const TargetInfoModal = ({
    topics,
    keywords,
    isVideo,
    onClose,
    onRemoveTopic,
    onRemoveKeyword,
}) => (
    <Modal
        title={`Your Selected Topics ${isVideo ? '' : '& Keywords'}`}
        onClose={onClose}
    >
        {topics.length > 0 && (
            <Box margin={['base', 0, 0]}>
                <Text size="large" weight="bolder">
                    Topics
                </Text>
                {topics.map((topic) => (
                    <Box
                        key={topic.id}
                        margin={['small', 'small', 0, 0]}
                        display="inline-block"
                    >
                        <TopicButton
                            logo={topic.group_logo}
                            action="remove"
                            theme="targeted"
                            onClick={() => onRemoveTopic(topic)}
                        >
                            {topic.name}
                        </TopicButton>
                    </Box>
                ))}
            </Box>
        )}

        {keywords.length > 0 && (
            <Box margin={['base', 0, 0]}>
                <Text size="large" weight="bolder">
                    Keywords
                </Text>
                {keywords.map((keyword) => (
                    <Box
                        key={keyword}
                        margin={['small', 'small', 0, 0]}
                        display="inline-block"
                    >
                        <Pill
                            theme="keyword"
                            suffixButton={<FontAwesomeIcon icon={faTimes} />}
                            onSuffixClick={() => onRemoveKeyword(keyword)}
                        >
                            {keyword}
                        </Pill>
                    </Box>
                ))}
            </Box>
        )}

        {!topics.length && !keywords.length && (
            <Alert theme="empty" title="No Active Targeting">
                You have removed all topics{!isVideo && ' and keywords'}.
            </Alert>
        )}
    </Modal>
);

const ScaleInfoModal = ({ score, changing, hasError, onClose, onReload }) => {
    const isUnknown = score === null;

    return (
        <Modal title="Scale" onClose={onClose}>
            <Text color={['gray', 'dark']}>
                <p>
                    Scale is an approximation of how much inventory will be
                    available to a campaign when using this context. The actual
                    scale of a campaign will be affected by other targeting
                    rules, as well as the spending policy of a campaign.
                </p>
            </Text>

            <Box padding={['large', 0, 0]}>
                <Row>
                    <Col span="auto">
                        <IndicatorBar
                            value={score}
                            lowMessage="Warning: Low Scale"
                            mediumMessage="Medium Scale"
                            highMessage="High Scale"
                            loading={changing}
                            loadingMessage="Calculating Scale"
                            unknownMessage="No Scale"
                            hasError={hasError}
                            errorMessage="Unable To Calculate Scale"
                            onReload={onReload}
                        />
                    </Col>
                </Row>
            </Box>

            <Box padding={['small', 0, 0]}>
                {!hasError && changing && (
                    <Text color={['gray', 'dark']}>
                        The scale of this context is being calculated.
                    </Text>
                )}

                {!hasError && !changing && isUnknown && (
                    <Text color={['gray', 'dark']}>
                        The scale of this context is unknown as there are no
                        active content topics or keywords on this context.
                    </Text>
                )}

                {!hasError && !changing && !isUnknown && (
                    <>
                        {score >= 0 && score < SCALE_LOW_LEVEL && (
                            <Text color={['gray', 'dark']}>
                                The scale of this context is low. This indicates
                                that there may be an insufficient amount of
                                inventory to run a small campaign successfully.
                            </Text>
                        )}

                        {score >= SCALE_LOW_LEVEL &&
                            score < SCALE_MEDIUM_LEVEL && (
                                <Text color={['gray', 'dark']}>
                                    The scale of this context is medium. This
                                    indicates that there may be a sufficient
                                    amount of inventory to run a small to medium
                                    sized campaign successfully.
                                </Text>
                            )}

                        {score >= SCALE_MEDIUM_LEVEL && (
                            <Text color={['gray', 'dark']}>
                                The scale of this context is high. This
                                indicates that there should be a sufficient
                                amount of inventory to run a typical campaign
                                successfully.
                            </Text>
                        )}
                    </>
                )}
            </Box>
        </Modal>
    );
};

const TargetInfo = ({ topics, keywords, cost, isVideo }) => {
    const count = topics.length + keywords.length;

    return (
        <Row alignItems="center" gutter="small">
            <Col>
                <div
                    className={cx({
                        counter: true,
                        disabled: !count,
                    })}
                >
                    {count}
                </div>
            </Col>
            <Col span="auto">
                {count > 0 && cost ? (
                    <Text size="small" color={['gray', 'dark']}>
                        Cost:{' '}
                        <Text weight="bold" inline>
                            {cost}
                        </Text>{' '}
                        CPM{' '}
                        <Text size="small" color={['gray', 'dark']} inline>
                            (
                            <Text textDecorationLine="line-through" inline>
                                {regularPricing[isVideo ? 'video' : 'display']}
                            </Text>
                            )
                        </Text>{' '}
                        <Box
                            display="inline-block"
                            background={['pink', 'base']}
                            padding="smallest"
                            borderRadius="base"
                        >
                            <Text
                                color={['gray', 'white']}
                                size="smaller"
                                weight="bold"
                            >
                                50% off
                            </Text>
                        </Box>
                    </Text>
                ) : null}
                <Text weight="bold" inline>
                    {(count > 0 && (isVideo ? 'Video' : 'Display')) ||
                        'No Active'}{' '}
                    Targeting
                </Text>
            </Col>
        </Row>
    );
};

const PageFooter = ({
    form,
    scaleScore,
    scaleError,
    scaleChanging,
    onFormChange,
    onGetScaleScore,
}) => {
    const { topics, keywords, is_video: isVideo } = form;

    const [showScaleInfoModal, setShowScaleInfoModal] = useState(false);
    const [showTargetInfoModal, setShowTargetInfoModal] = useState(false);

    const removeTopic = (topic) => {
        onFormChange({
            ...form,
            topics: form.topics.filter(({ id }) => id !== topic.id),
        });
    };
    const removeKeyword = (keyword) => {
        onFormChange({
            ...form,
            keywords: form.keywords.filter((item) => item !== keyword),
        });
    };

    return (
        <div className={styles['footer-bar']}>
            <Container size="medium">
                <Row alignItems="center" gutter="small" gap="small">
                    <Col span="auto">
                        <ClickArea
                            isDisabled={!topics.length && !keywords.length}
                            onClick={() =>
                                setShowTargetInfoModal(!showTargetInfoModal)
                            }
                        >
                            <TargetInfo
                                topics={topics}
                                keywords={keywords}
                                cost={
                                    form.integration?.[
                                        isVideo
                                            ? 'video_targeting_cpm_usd'
                                            : 'display_targeting_cpm_usd'
                                    ]
                                }
                                isVideo={isVideo}
                                onFormChange
                            />
                        </ClickArea>
                    </Col>

                    <Col />

                    <Col span="auto">
                        <IndicatorBar
                            value={scaleScore}
                            lowMessage="Warning: Low Scale"
                            mediumMessage="Medium Scale"
                            highMessage="High Scale"
                            loading={scaleChanging}
                            loadingMessage="Calculating Scale"
                            unknownMessage="No Scale"
                            hasError={!!scaleError}
                            errorMessage={scaleError}
                            onClick={() => setShowScaleInfoModal(true)}
                            onHelp={() => setShowScaleInfoModal(true)}
                            onReload={onGetScaleScore}
                        />
                    </Col>
                </Row>
            </Container>

            {showTargetInfoModal && (
                <TargetInfoModal
                    topics={form.topics}
                    keywords={form.keywords}
                    isVideo={isVideo}
                    onClose={() => setShowTargetInfoModal(false)}
                    onRemoveTopic={removeTopic}
                    onRemoveKeyword={removeKeyword}
                />
            )}
            {showScaleInfoModal && (
                <ScaleInfoModal
                    score={scaleScore}
                    changing={scaleChanging}
                    hasError={!!scaleError}
                    onClose={() => setShowScaleInfoModal(false)}
                    onReload={onGetScaleScore}
                />
            )}
        </div>
    );
};

export default PageFooter;
